import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { CoachOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react"
import type { ReactNode } from "react"

const TOTAL_STEPS = 3

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="flex gap-2">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => (
        <div
          key={i}
          className={cn(
            "h-4 w-14 rounded-lg",
            i < step - 1 ? "bg-(--badge-primary-bg-subtle)" : "bg-(--badge-neutral-bg-subtle)",
          )}
        />
      ))}
    </div>
  )
}

type CoachOfferFormLayoutProps = {
  type: CoachOfferType
  step: number
  onSecondaryAction: () => void
  onPrimaryAction: () => Promise<void> | void
  isModifyingOffer: boolean
  children: ReactNode
}

export function CoachOfferFormLayout({
  type,
  step,
  onSecondaryAction,
  onPrimaryAction,
  isModifyingOffer,
  children,
}: CoachOfferFormLayoutProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-2 px-5 pt-18 pb-4">
        <StepIndicator step={step} />
        <div className="flex flex-col gap-2.5">
          <Text fontStyle="TL">{getTitle(type, isModifyingOffer)}</Text>
          <Text fontStyle="BM" className="text-textOnSurface-subtle">
            {getSubtitle(type)}
          </Text>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-5 py-4">{children}</div>

      <div className="sticky bottom-0 flex flex-row gap-8.5 border-t border-borderDefault bg-surface-background px-5 py-4">
        <Button variant="outline" onClick={onSecondaryAction} className="flex-1" size="lg">
          {step > 2 && <ArrowLeftIcon size={16} />}
          {step === 2 ? word("global.cancel") : word("global.previous")}
        </Button>
        <Button variant="primary" onClick={() => void onPrimaryAction()} className="flex-1 gap-2" size="lg">
          {word("global.next")}
          <ArrowRightIcon size={16} />
        </Button>
      </div>
    </div>
  )
}

function getTitle(type: CoachOfferType, isModifyingOffer: boolean): string {
  switch (type) {
    case CoachOfferType.Individual:
      return isModifyingOffer
        ? word("coach.offers.form.title.individual.modify")
        : word("coach.offers.form.title.individual.create")
    case CoachOfferType.Collective:
      return isModifyingOffer
        ? word("coach.offers.form.title.collective.modify")
        : word("coach.offers.form.title.collective.create")
  }
}

function getSubtitle(type: CoachOfferType): string {
  switch (type) {
    case CoachOfferType.Individual:
      return word("coach.offers.form.subtitle.individual")
    case CoachOfferType.Collective:
      return word("coach.offers.form.subtitle.collective")
  }
}
